import { useSeo } from "@/hooks/useSeo";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useProduct, useProducts } from "@/hooks/useProducts";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingBag, ArrowLeft, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const GIFT_CONTENTS: Record<string, string[]> = {
  "blush-trio-gift-set": ["Ivory", "Blush", "Noir"],
  "scarlet-trio-gift-set": ["Navy", "Scarlet", "Ivory"],
  "noir-trio-gift-set": ["Black", "Tan", "Forest"],
};

const ProductPage = () => {
  const { handle } = useParams<{ handle: string }>();
  const { data: product, isLoading, error } = useProduct(handle || "");
  const { data: rawAll } = useProducts(20);
  const isGift = /gift-set/i.test(handle || "");
  const allProducts = rawAll?.filter((p) => /gift set/i.test(p.node.title) === isGift);
  const boxContents = GIFT_CONTENTS[handle || ""];
  useSeo(
    product ? (isGift ? `${product.title} | Three 10ml Vegan Leather Atomizers | Luxe Lords` : `${product.title} 10ml Vegan Leather Perfume Atomizer | Luxe Lords`) : undefined,
    product ? (isGift ? `${product.title}: three 10ml refillable perfume atomizers in premium vegan leather, presented in a gift box. £58.99.` : `${product.title} 10ml refillable perfume atomizer in premium vegan leather. £24.95.`) : undefined,
    `/product/${handle}`,
  );
  const addItem = useCartStore(state => state.addItem);
  const isAddingToCart = useCartStore(state => state.isLoading);
  
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    setSelectedVariantIndex(0);
    setSelectedImage(0);
    setQuantity(1);
  }, [handle]);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <div className="space-y-6">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/4" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 container mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-3xl mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/" className="btn-luxury">
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = product.images.edges;
  const variants = product.variants.edges;
  const selectedVariant = variants[selectedVariantIndex]?.node;
  const price = selectedVariant?.price || product.priceRange.minVariantPrice;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description,
    image: images.map((i) => i.node.url),
    brand: { "@type": "Brand", name: "Luxe Lords" },
    offers: {
      "@type": "Offer",
      url: `https://luxelords.co.uk/product/${handle}`,
      price: Number(price.amount).toFixed(2),
      priceCurrency: price.currencyCode,
      availability: selectedVariant?.availableForSale ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    await addItem({
      product: { node: product },
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity,
      selectedOptions: selectedVariant.selectedOptions || []
    });
    
    toast.success("Added to cart", {
      description: `${product.title} x ${quantity}`,
    });
  };

  return (
    <div className="min-h-screen">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd).replace(/</g, "\\u003c") }} />
      <Navigation />
      
      <div className="pt-32 pb-24 container mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <Link 
          to={isGift ? "/#gift-sets" : "/shop"} 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isGift ? "Back to Gift Sets" : "Back to Collection"}
        </Link>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-square bg-secondary/30 rounded-lg overflow-hidden">
              {images[selectedImage]?.node ? (
                <img
                  src={images[selectedImage].node.url}
                  alt={images[selectedImage].node.altText || product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No image
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === idx ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img
                      src={img.node.url}
                      alt={img.node.altText || `${product.title} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="font-serif text-4xl lg:text-5xl text-foreground mb-4">
                {product.title}
              </h1>
              <p className="text-2xl font-medium">
                {price.currencyCode} {parseFloat(price.amount).toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Free UK delivery · Next day £2.95</p>
            </div>

            {/* Colour picker */}
            {allProducts && allProducts.length > 1 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">
                  {isGift ? "Gift set" : "Colour"}: <span className="text-muted-foreground font-normal">{product.title}</span>
                </p>
                <div className={`grid ${isGift ? "grid-cols-3" : "grid-cols-4"} gap-3`}>
                  {allProducts.map((p) => {
                    const img = p.node.images.edges[0]?.node;
                    const active = p.node.handle === product.handle;
                    const pr = p.node.priceRange.minVariantPrice;
                    return (
                      <Link
                        key={p.node.id}
                        to={`/product/${p.node.handle}`}
                        replace
                        aria-current={active ? "true" : undefined}
                        className="group text-center"
                      >
                        <div className={`aspect-square rounded-md overflow-hidden border-2 transition-colors ${active ? "border-primary" : "border-transparent group-hover:border-border"}`}>
                          {img && (
                            <img src={`${img.url}&width=200`} alt={`${p.node.title} atomizer`} loading="lazy" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <span className="block text-xs mt-1 text-foreground">{p.node.title}</span>
                        <span className="block text-xs text-muted-foreground">£{parseFloat(pr.amount).toFixed(2)}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {product.description && (
              <p className="text-muted-foreground leading-relaxed">
                {product.description}
              </p>
            )}

            {isGift && (
              <div className="border-t border-b border-border py-5 space-y-3">
                <p className="text-luxury text-muted-foreground">What's in the box</p>
                <ul className="space-y-2 text-sm text-foreground">
                  {boxContents ? (
                    <li>Three 10ml atomizers in {boxContents.slice(0, -1).join(", ")} and {boxContents[boxContents.length - 1]} Premium Vegan Leather</li>
                  ) : (
                    <li>Three 10ml Premium Vegan Leather atomizers</li>
                  )}
                  <li>Signature Luxe Lords presentation gift box</li>
                  <li>Refillable, leak-proof and travel-ready</li>
                </ul>
              </div>
            )}

            {/* Variant Selection */}
            {variants.length > 1 && (
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  {product.options[0]?.name || "Options"}
                </label>
                <div className="flex flex-wrap gap-2">
                  {variants.map((variant, idx) => (
                    <button
                      key={variant.node.id}
                      onClick={() => setSelectedVariantIndex(idx)}
                      disabled={!variant.node.availableForSale}
                      className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                        selectedVariantIndex === idx
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:border-primary"
                      } ${!variant.node.availableForSale ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {variant.node.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Quantity</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={isAddingToCart || !selectedVariant?.availableForSale}
              size="lg"
              className="w-full text-lg py-6"
            >
              {isAddingToCart ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Add to Cart — {price.currencyCode} {(parseFloat(price.amount) * quantity).toFixed(2)}
                </>
              )}
            </Button>

            {!selectedVariant?.availableForSale && (
              <p className="text-sm text-muted-foreground text-center">
                This variant is currently out of stock.
              </p>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProductPage;
